import {Controller} from "@hotwired/stimulus"

/**
 * Flutter Permissions Controller
 *
 * Centralized permission management for Flutter WebView integration.
 * Handles: contacts, camera, microphone permissions with on-demand requests.
 *
 * Usage:
 *   - Import this controller's methods in other controllers
 *   - Or use the global FlutterPermissions helper
 */
export default class extends Controller {
    static PERMISSION_KEYS = {
        contacts: "flutter_contacts_permission",
        camera: "flutter_camera_permission",
        microphone: "flutter_microphone_permission"
    }

    static PERMISSION_STATUS = {
        granted: "granted",
        denied: "denied",
        unknown: "unknown"
    }

    /**
     * Check if we're running inside Flutter WebView
     */
    get isInApp() {
        return typeof window.flutter_inappwebview !== "undefined"
    }

    connect() {
        // Expose helper globally for use by other controllers
        window.FlutterPermissions = {
            isInApp: this.isInApp,
            checkPermission: this.checkPermission.bind(this),
            requestPermission: this.requestPermission.bind(this),
            hasPermission: this.hasPermission.bind(this),
            isDenied: this.isDenied.bind(this),
            requestContacts: this.requestContactsPermission.bind(this),
            requestCamera: this.requestCameraPermission.bind(this),
            requestMicrophone: this.requestMicrophonePermission.bind(this),
            importContacts: this.importContacts.bind(this)
        }
    }

    /**
     * Get stored permission status from localStorage
     */
    getStoredPermission(permissionType) {
        const key = this.constructor.PERMISSION_KEYS[permissionType]
        return localStorage.getItem(key) || this.constructor.PERMISSION_STATUS.unknown
    }

    /**
     * Store permission status in localStorage
     */
    setStoredPermission(permissionType, status) {
        const key = this.constructor.PERMISSION_KEYS[permissionType]
        localStorage.setItem(key, status)
    }

    /**
     * Check if permission was previously denied
     */
    isDenied(permissionType) {
        return this.getStoredPermission(permissionType) === this.constructor.PERMISSION_STATUS.denied
    }

    /**
     * Check if permission was previously granted
     */
    hasPermission(permissionType) {
        return this.getStoredPermission(permissionType) === this.constructor.PERMISSION_STATUS.granted
    }

    /**
     * Check permission status from Flutter
     * Returns: { granted: boolean, status: string }
     */
    async checkPermission(permissionType) {
        if (!this.isInApp) {
            return {granted: false, status: "not_in_app"}
        }

        try {
            const handlerName = `check${this.capitalize(permissionType)}Permission`
            const result = await window.flutter_inappwebview.callHandler(handlerName)

            const granted = result?.granted || result === true
            const status = granted ?
                this.constructor.PERMISSION_STATUS.granted :
                this.constructor.PERMISSION_STATUS.denied

            this.setStoredPermission(permissionType, status)
            return {granted, status}
        } catch (error) {
            console.error(`Error checking ${permissionType} permission:`, error)
            return {granted: false, status: "error"}
        }
    }

    /**
     * Request permission from Flutter
     * Returns: { granted: boolean, status: string }
     */
    async requestPermission(permissionType) {
        if (!this.isInApp) {
            return {granted: false, status: "not_in_app"}
        }

        try {
            const handlerName = `request${this.capitalize(permissionType)}Permission`
            const result = await window.flutter_inappwebview.callHandler(handlerName)

            const granted = result?.granted || result === true
            const status = granted ?
                this.constructor.PERMISSION_STATUS.granted :
                this.constructor.PERMISSION_STATUS.denied

            this.setStoredPermission(permissionType, status)
            return {granted, status}
        } catch (error) {
            console.error(`Error requesting ${permissionType} permission:`, error)
            return {granted: false, status: "error"}
        }
    }

    /**
     * Request contacts permission specifically
     */
    async requestContactsPermission() {
        return await this.requestPermission("contacts")
    }

    /**
     * Request camera permission specifically
     */
    async requestCameraPermission() {
        return await this.requestPermission("camera")
    }

    /**
     * Request microphone permission specifically
     */
    async requestMicrophonePermission() {
        return await this.requestPermission("microphone")
    }

    /**
     * Import contacts from device (with permission check/request)
     * Returns: { success: boolean, contacts: array, permissionDenied: boolean }
     */
    async importContacts() {
        if (!this.isInApp) {
            return {success: false, contacts: [], error: "Not in app"}
        }

        // Check if previously denied
        if (this.isDenied("contacts")) {
            return {success: false, contacts: [], permissionDenied: true}
        }

        try {
            const result = await window.flutter_inappwebview.callHandler("importContacts")

            if (!result) {
                return {success: false, contacts: [], error: "No response"}
            }

            // Handle permission denied
            if (result.success === false) {
                if (result.permissionDenied || result.permission_denied) {
                    this.setStoredPermission("contacts", this.constructor.PERMISSION_STATUS.denied)
                    return {success: false, contacts: [], permissionDenied: true}
                }
                return {success: false, contacts: [], error: result.error}
            }

            // Mark permission as granted
            this.setStoredPermission("contacts", this.constructor.PERMISSION_STATUS.granted)

            // Extract contacts from various response formats
            let contacts = []
            if (result.contacts && Array.isArray(result.contacts)) {
                contacts = result.contacts
            } else if (Array.isArray(result)) {
                contacts = result
            } else if (result.data && Array.isArray(result.data)) {
                contacts = result.data
            }

            return {success: true, contacts}
        } catch (error) {
            console.error("Error importing contacts:", error)
            return {success: false, contacts: [], error: error.message}
        }
    }

    /**
     * Helper to capitalize first letter
     */
    capitalize(str) {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }
}

